import { makeStyles } from "@material-ui/core";
import React, { useCallback, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { ValueType } from "react-select/src/types";

import { ApiResult } from "../../rest/baseApi";

const useStyles = makeStyles(() => ({
    container: {
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
        margin: "20px 0px",
    },
    userSelectControl: {
        minWidth: 400,
        marginLeft: "10px",
    },
}));

interface OptionType { value: string; label: string; }

interface Props {
    selectedValue: string | undefined;
    setSelectedValue: (value: string | undefined) => void;
    dealerId: number | undefined;
    organizationId: number | undefined;
    siteId: number | undefined;
    searchMethod: (
        searchValue: string,
        dealerId?: number,
        organizationId?: number,
        siteId?: number,
    ) => Promise<ApiResult<string[]>>;
    label: string;
}

export function SearchSelect({
    selectedValue,
    setSelectedValue,
    dealerId,
    organizationId,
    siteId,
    searchMethod,
    label,
}: Props) {
    const classes = useStyles();

    const loadOptions = useCallback(async (searchValue: string) => {
        try {
            const response = await searchMethod(
                searchValue,
                dealerId || undefined,
                organizationId || undefined,
                siteId || undefined
            );

            return response.data ? response.data.map((s) => ({ label: s, value: s })) : [];
        } catch (error) {
            console.error(`Error fetching SearchSelect ${label} options:`, error);
            return [];
        }
    }, [searchMethod, organizationId, siteId, label, dealerId]);

    const onChange = useCallback((option: ValueType<OptionType>) => {
        setSelectedValue(option ? (option as OptionType).value : undefined);
    }, [setSelectedValue]);

    const value: OptionType | undefined = useMemo(() => {
        if (!selectedValue) return;
        return { value: selectedValue, label: selectedValue };
    }, [selectedValue]);

    const noOptionsMessage = useCallback((obj: { inputValue: string }) => {
        return obj.inputValue ? "No matches found" : "Start typing to search users";
    }, []);

    return (
        <div className={classes.container}>
            {label}{" : "}
            <AsyncSelect
                value={value}
                onChange={onChange}
                className={classes.userSelectControl}
                controlShouldRenderValue={Boolean(value)}
                loadOptions={loadOptions}
                noOptionsMessage={noOptionsMessage}
                cacheOptions={true}
                isClearable={true}
            />
        </div>
    );
}
